import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "urql";
import { Button, clsx } from "@gemlightbox/core-kit";
import { useStores } from "src/hooks";
import { DEVICE_FIRMWARE_LIST } from "src/api/graphql-api/camera/camera.gql";
import {
  blobToBase64,
  appVersionComparisonPlus,
  calculateBlobSha256,
} from "src/utils/common.utils";
import { observer } from "mobx-react-lite";
import styles from "./styles.module.css";
import FirmwareUpgradeBg from "src/external-ts/assets/images/camera/firmwareUpgradeBg.png";
import { ReactComponent as UpgradeIcon } from "src/external-ts/assets/images/camera/firmware-upgrade-icon.svg";
import { ReactComponent as SuccessIcon } from "src/external-ts/assets/images/camera/firmware-upgrade-success-icon.svg";
import { ReactComponent as CloseIcon } from "src/external-ts/assets/images/camera/firmware-upgrade-close-icon.svg";
import { ReactComponent as DisconnectedIcon } from "src/external-ts/assets/images/camera/bluetooth-disconnected-white-icon.svg";
import { cameraStore } from "../camera.store";
import { DeviceModel } from "src/models";
import { FilterMode } from "../camera.types";

const useSessionStorageState = <T,>(key: string, options: { defaultValue: T }) => {
  const [state, setState] = useState<T>(() => {
    const storedValue = sessionStorage.getItem(key);
    if (storedValue) {
      try {
        return JSON.parse(storedValue);
      } catch (e) {
        return options.defaultValue;
      }
    }
    return options.defaultValue;
  });

  useEffect(() => {
    sessionStorage.setItem(key, JSON.stringify(state));
  }, [key, state]);

  return [state, setState] as const;
};

const extractBase64Payload = (base64: string) => {
  return base64.split(",")[1] || base64;
};

type ConfirmDialogProps = {
  className?: string;
  icon: React.ReactNode;
  contentClassName?: string;
  title: string;
  message: string;
  titleClassName?: string;
  buttonsClassName?: string;
  cancelBtn?: string;
  confirmBtnClassName?: string;
  confirmBtn: string;
  loading?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
};

const ConfirmDialog = ({
  className,
  icon,
  contentClassName,
  title,
  message,
  titleClassName,
  buttonsClassName,
  cancelBtn,
  confirmBtnClassName,
  confirmBtn,
  loading = false,
  onClose,
  onConfirm,
}: ConfirmDialogProps) => (
  <div
    className={clsx(styles.dialogContainer, className)}
    style={{
      backgroundImage: `url(${FirmwareUpgradeBg})`,
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
    }}
  >
    <div className={styles.dialogIcon}>{icon}</div>
    <div className={clsx(styles.dialogContent, contentClassName)}>
      <span className={clsx(styles.dialogTitle, titleClassName)}>{title}</span>
      <span className={styles.dialogMessage}>{message}</span>
    </div>
    <div className={clsx(styles.dialogButtons, buttonsClassName)}>
      {cancelBtn && onClose && (
        <Button
          // @ts-ignore
          appearance="ghost"
          className={styles.cancelBtn}
          onClick={onClose}
        >
          {cancelBtn}
        </Button>
      )}
      {confirmBtn && onConfirm && (
        <Button
          className={clsx(styles.confirmBtn, confirmBtnClassName)}
          onClick={onConfirm}
          loading={loading}
          appearance="primary"
        >
          {confirmBtn}
        </Button>
      )}
    </div>
  </div>
);

type UpgradeProgressProps = {
  progress: number;
};

const UpgradeProgress = observer(({ progress }: UpgradeProgressProps) => {
  const { localeStore } = useStores();
  return (
    <div
      className={styles.dialogContainer}
      style={{
        width: "34rem",
        backgroundImage: `url(${FirmwareUpgradeBg})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className={styles.dialogContent}>
        <span className={styles.dialogTitle}>
          {localeStore.t("camera.glbFirmwareUpgrade.updating.title" as any)}
        </span>
        <span className={styles.dialogMessage}>
          {localeStore.t("camera.glbFirmwareUpgrade.updating.description" as any)}
        </span>
      </div>
      <div className={styles.progressContainer}>
        <div className={styles.progressBarBg}>
          <div className={styles.progressBarFill} style={{ width: `${progress}%` }}></div>
        </div>
        <div className={styles.progressText}>
          {localeStore
            .t("camera.glbFirmwareUpgrade.updating.progress" as any)
            .replace("{{progress}}", String(progress))}
        </div>
      </div>
    </div>
  );
});

enum UpdateStatus {
  Ready,
  Updating,
  Success,
  Failure,
  Disconnected,
}

const useLatestFirmware = (modelName: string, versionName: string) => {
  const [{ data }] = useQuery({
    query: DEVICE_FIRMWARE_LIST,
    pause: !versionName,
    variables: {
      input: {
        modelName,
        versionName,
      },
    },
  });

  return data?.deviceFirmwareList?.data?.[0] || null;
};

const GlbFirmwareUpgrade = observer(() => {
  const [isLater, setIsLater] = useSessionStorageState("glbFirmwareUpgradeIsLater", {
    defaultValue: false,
  });
  const [progress, setProgress] = useState(0);
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>(UpdateStatus.Ready);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [firmwareData, setFirmwareData] = useState<string | null>(null);

  const [needUpgrade, setNeedUpgrade] = useState(false);
  const { appCallbackStore, localeStore } = useStores();
  const hasConnectDevice = cameraStore.connectionStatus === "connected";
  const [firmwareVersion, setFirmwareVersion] = useState("");
  const refBluetoothDevice = useRef("");
  const upgradeInFlightRef = useRef(false);
  const previousHasConnectDeviceRef = useRef(hasConnectDevice);
  const latestFirmware = useLatestFirmware("GemLightboxMax", firmwareVersion);

  const bluetoothDevice = cameraStore.bluetoothDeviceSelected;
  const isDeviceModel = (d: any): d is DeviceModel => d && typeof d.type === "number";

  const deviceType = isDeviceModel(bluetoothDevice) ? bluetoothDevice.type : undefined;
  const selectBluetoothDevice = isDeviceModel(bluetoothDevice)
    ? bluetoothDevice.identifier
    : (bluetoothDevice as any)?.id;

  const isGemLightboxMax = useMemo(() => {
    const matchType = deviceType === FilterMode.GemLightboxMax;
    const matchFilter = cameraStore.filterValue === FilterMode.GemLightboxMax;
    return (matchType || matchFilter) && selectBluetoothDevice && hasConnectDevice;
  }, [deviceType, selectBluetoothDevice, hasConnectDevice, cameraStore.filterValue]);

  useEffect(() => {
    if (!latestFirmware) return;
    const fetchFirmwareData = async () => {
      try {
        const response = await fetch(latestFirmware.fileUrl);
        const blob = await response.blob();
        const firmwareSha256 = await calculateBlobSha256(blob);
        const expectedChecksum =
          typeof latestFirmware.checksum === "string" ? latestFirmware.checksum.toLowerCase() : "";

        if (expectedChecksum && firmwareSha256 !== expectedChecksum) {
          setIsChecked(false);
          return;
        }
        setIsChecked(true);
        const data = (await blobToBase64(blob)) as string;
        setFirmwareData(data);
      } catch (error) {
        console.error(error);
        setIsChecked(false);
        setFirmwareData(null);
      }
    };
    fetchFirmwareData();
  }, [latestFirmware]);

  useEffect(() => {
    if (isGemLightboxMax && selectBluetoothDevice) {
      refBluetoothDevice.current = selectBluetoothDevice;
      appCallbackStore.getBluetoothTurntableVersion(selectBluetoothDevice, (res) => {
        if (res?.fwVersion) {
          setFirmwareVersion(res.fwVersion);
        }
      });
    }
  }, [isGemLightboxMax, selectBluetoothDevice, appCallbackStore]);

  const handleUpgrade = useCallback(async () => {
    if (!isChecked || !hasConnectDevice || !firmwareData) return;
    if (upgradeInFlightRef.current || updateStatus === UpdateStatus.Updating) return;
    upgradeInFlightRef.current = true;
    setProgress(0);
    setUpdateStatus(UpdateStatus.Updating);

    try {
      appCallbackStore.glbFirmwareUpgrade(
        extractBase64Payload(firmwareData),
        selectBluetoothDevice,
        (res) => {
          if (res?.status === "progress") {
            setProgress(res.percent);
          } else if (res?.status === "completed") {
            setUpdateStatus(UpdateStatus.Success);
            setFirmwareData(null);
          }
        },
        (error) => {
          upgradeInFlightRef.current = false;
          setUpdateStatus((prev) =>
            prev === UpdateStatus.Disconnected ? prev : UpdateStatus.Failure,
          );
          console.warn(error);
        },
      );
    } catch {
      upgradeInFlightRef.current = false;
      setUpdateStatus(UpdateStatus.Failure);
    }
  }, [
    isChecked,
    firmwareData,
    selectBluetoothDevice,
    hasConnectDevice,
    updateStatus,
    appCallbackStore,
  ]);

  useEffect(() => {
    if (!appVersionComparisonPlus("1.0.28.0", "1.1.30")) return;
    if (!isChecked || !firmwareData) return;

    setProgress(0);

    if (isGemLightboxMax) {
      setNeedUpgrade(true);
      setUpdateStatus(UpdateStatus.Ready);
      return;
    }

    setUpdateStatus((prev) => (prev === UpdateStatus.Success ? prev : UpdateStatus.Disconnected));
  }, [isGemLightboxMax, isChecked, firmwareData]);

  useEffect(() => {
    const wasConnected = previousHasConnectDeviceRef.current;

    if (isReconnecting && !wasConnected && hasConnectDevice) {
      setIsReconnecting(false);
    }

    previousHasConnectDeviceRef.current = hasConnectDevice;
  }, [hasConnectDevice, isReconnecting]);

  useEffect(() => {
    if (updateStatus !== UpdateStatus.Updating) {
      upgradeInFlightRef.current = false;
    }

    if (
      updateStatus === UpdateStatus.Failure ||
      updateStatus === UpdateStatus.Disconnected ||
      updateStatus === UpdateStatus.Ready
    ) {
      setProgress(0);
    }
  }, [updateStatus]);

  if (isLater || !needUpgrade || !isChecked) return null;

  return (
    <div className={styles.overlay}>
      {updateStatus === UpdateStatus.Ready && (
        <ConfirmDialog
          icon={<UpgradeIcon width={64} height={64} />}
          title={localeStore.t("camera.glbFirmwareUpgrade.update.title" as any)}
          message={localeStore.t("camera.glbFirmwareUpgrade.update.description" as any)}
          cancelBtn={
            !latestFirmware?.classification || latestFirmware?.classification === "required"
              ? localeStore.t("camera.glbFirmwareUpgrade.update.cancel" as any)
              : ""
          }
          confirmBtn={localeStore.t("camera.glbFirmwareUpgrade.update.confirm" as any)}
          onConfirm={handleUpgrade}
          onClose={() => {
            setIsLater(true);
            setNeedUpgrade(false);
          }}
        />
      )}
      {updateStatus === UpdateStatus.Updating && <UpgradeProgress progress={progress} />}
      {updateStatus === UpdateStatus.Success && (
        <ConfirmDialog
          className={styles.successDialog}
          icon={
            <div className={styles.successIconWrapper}>
              <SuccessIcon />
            </div>
          }
          contentClassName={styles.successContent}
          title={localeStore.t("camera.glbFirmwareUpgrade.updateSuccessful.title" as any)}
          message={localeStore.t("camera.glbFirmwareUpgrade.updateSuccessful.description" as any)}
          confirmBtn={localeStore.t("camera.glbFirmwareUpgrade.updateSuccessful.confirm" as any)}
          confirmBtnClassName={styles.successConfirmBtn}
          onConfirm={() => {
            setNeedUpgrade(false);
            setIsChecked(false);
            setFirmwareData(null);
          }}
        />
      )}
      {updateStatus === UpdateStatus.Failure && (
        <ConfirmDialog
          className={styles.successDialog}
          icon={
            <div className={styles.failureIconWrapper}>
              <CloseIcon />
            </div>
          }
          contentClassName={styles.successContent}
          title={localeStore.t("camera.glbFirmwareUpgrade.updateFailed.title" as any)}
          message={localeStore.t("camera.glbFirmwareUpgrade.updateFailed.description" as any)}
          titleClassName={styles.failureTitle}
          buttonsClassName={styles.wideButtons}
          cancelBtn={
            !latestFirmware?.classification || latestFirmware?.classification === "required"
              ? localeStore.t("camera.glbFirmwareUpgrade.updateFailed.cancel" as any)
              : ""
          }
          confirmBtn={localeStore.t("camera.glbFirmwareUpgrade.updateFailed.confirm" as any)}
          onConfirm={handleUpgrade}
          onClose={() => {
            setIsLater(true);
            setNeedUpgrade(false);
          }}
        />
      )}
      {updateStatus === UpdateStatus.Disconnected && (
        <ConfirmDialog
          className={styles.successDialog}
          icon={
            <div className={styles.failureIconWrapper}>
              <DisconnectedIcon />
            </div>
          }
          contentClassName={styles.successContent}
          title={localeStore.t("camera.glbFirmwareUpgrade.disconnected.title" as any)}
          message={localeStore.t("camera.glbFirmwareUpgrade.disconnected.description" as any)}
          titleClassName={styles.failureTitle}
          buttonsClassName={styles.wideButtons}
          confirmBtn={localeStore.t("camera.glbFirmwareUpgrade.disconnected.confirm" as any)}
          loading={isReconnecting}
          onConfirm={() => {
            setIsReconnecting(true);
            if (refBluetoothDevice.current)
              appCallbackStore.connectBuletoothDevice(refBluetoothDevice.current);
          }}
          cancelBtn={
            !latestFirmware?.classification || latestFirmware?.classification === "required"
              ? localeStore.t("camera.glbFirmwareUpgrade.disconnected.cancel" as any)
              : ""
          }
          onClose={() => {
            setIsLater(true);
            setNeedUpgrade(false);
          }}
        />
      )}
    </div>
  );
});

export default GlbFirmwareUpgrade;
